import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Download, Share2, Copy, Image, Video, Mic, FileText, Loader, Eye } from 'lucide-react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { addToQueue, fileToBase64, getQueue, processQueue, registerSync, onSyncSuccess } from '../../utils/offlineQueue';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');

// Resolve URL - prepend backend for local files
function resolveUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BACKEND_URL}${url}`;
  return url;
}

// ═══════════════════════════════════════
// FILE UPLOAD with offline fallback
// ═══════════════════════════════════════
export function FileUpload({ entityType, entityId, onUploaded, multiple = true, accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(Math.round(((i) / files.length) * 100));

      if (!navigator.onLine) {
        const base64 = await fileToBase64(file);
        await addToQueue({ type: 'media_upload', entity_type: entityType, entity_id: entityId, fileData: base64, fileName: file.name, mimeType: file.type });
        registerSync();
        toast.success(`${file.name} queued offline`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);
        await api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`${file.name} uploaded`);
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }

    setProgress(100);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    if (onUploaded) onUploaded();
  };

  return (
    <div className="relative">
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={handleFiles} className="hidden" id={`upload-${entityType}-${entityId}`} />
      <label htmlFor={`upload-${entityType}-${entityId}`}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer transition">
        {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
        {uploading ? `Uploading ${progress}%` : 'Attach files'}
      </label>
    </div>
  );
}

// ═══════════════════════════════════════
// MEDIA VIEWER — shows thumbnails, players
// ═══════════════════════════════════════
export function MediaViewer({ attachments = [] }) {
  if (!attachments.length) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((att) => (
        <MediaThumb key={att.id} attachment={att} />
      ))}
    </div>
  );
}

function MediaThumb({ attachment }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [url, setUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [triedApiFallback, setTriedApiFallback] = useState(false);

  // Auto-resolve URL on mount
  useEffect(() => {
    const loadUrl = async () => {
      // First try s3_url directly
      const direct = resolveUrl(attachment.s3_url);
      if (direct) { setUrl(direct); setLoaded(true); return; }
      // Fallback to API
      try {
        const { data } = await api.get(`/media/${attachment.id}/url`);
        setUrl(resolveUrl(data.url));
      } catch {
        setUrl(null);
      }
      setLoaded(true);
    };
    loadUrl();
  }, [attachment.id, attachment.s3_url]);

  // When image fails to load, try API fallback URL before giving up
  const handleImgError = async (e) => {
    if (!triedApiFallback) {
      setTriedApiFallback(true);
      try {
        const { data } = await api.get(`/media/${attachment.id}/url`);
        const fallbackUrl = resolveUrl(data.url);
        if (fallbackUrl && fallbackUrl !== url) {
          setUrl(fallbackUrl);
          return; // Let the img retry with new URL
        }
      } catch {}
    }
    setImgError(true);
  };

  const getUrl = async () => {
    if (url) return url;
    try {
      const { data } = await api.get(`/media/${attachment.id}/url`);
      const resolved = resolveUrl(data.url);
      setUrl(resolved);
      return resolved;
    } catch {
      return resolveUrl(attachment.s3_url);
    }
  };

  const handleDownload = async () => {
    const fileUrl = await getUrl();
    if (!fileUrl) { toast.error('File not available'); return; }
    window.open(fileUrl, '_blank');
  };

  const handleCopy = async () => {
    const fileUrl = await getUrl();
    if (!fileUrl) { toast.error('File not available'); return; }
    try { await navigator.clipboard.writeText(fileUrl); toast.success('Link copied!'); } catch {
      const input = document.createElement('input'); input.value = fileUrl; document.body.appendChild(input); input.select(); document.execCommand('copy'); document.body.removeChild(input); toast.success('Link copied!');
    }
  };

  const handleShare = async (platform) => {
    const fileUrl = await getUrl();
    if (!fileUrl) { toast.error('File not available'); return; }
    const text = `${attachment.file_name || 'File'} — Interior Point PMS`;
    if (platform === 'native' && navigator.share) { try { await navigator.share({ title: text, url: fileUrl }); return; } catch {} }
    if (platform === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + fileUrl)}`, '_blank');
    else if (platform === 'imessage') window.open(`sms:&body=${encodeURIComponent(text + '\n' + fileUrl)}`, '_blank');
    setShowMenu(false);
  };

  // Render based on file type
  const isImage = attachment.file_type === 'image';
  const isVideo = attachment.file_type === 'video';
  const isAudio = attachment.file_type === 'voice_note';

  // Image thumbnail
  if (isImage && url) {
    return (
      <div className="relative group">
        <div className="w-20 h-20 rounded-lg overflow-hidden border cursor-pointer hover:opacity-90" onClick={() => !imgError && setShowPreview(true)}>
          {imgError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
              <Image size={20} />
              <span className="text-[9px] mt-0.5 truncate max-w-[70px]">{attachment.file_name || 'Image'}</span>
            </div>
          ) : (
            <img src={url} alt={attachment.file_name} className="w-full h-full object-cover" onError={handleImgError} />
          )}
        </div>
        <ActionButtons onDownload={handleDownload} onCopy={handleCopy} onShare={() => setShowMenu(!showMenu)} />
        {showMenu && <ShareMenu onShare={handleShare} onCopy={handleCopy} onDownload={handleDownload} onClose={() => setShowMenu(false)} />}
        {showPreview && !imgError && <ImagePreview url={url} name={attachment.file_name} onClose={() => setShowPreview(false)} onDownload={handleDownload} onShare={handleShare} onCopy={handleCopy} />}
      </div>
    );
  }

  // Image without URL — show placeholder
  if (isImage && !url && loaded) {
    return (
      <div className="relative group">
        <div className="w-20 h-20 rounded-lg overflow-hidden border">
          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
            <Image size={20} />
            <span className="text-[9px] mt-0.5">Unavailable</span>
          </div>
        </div>
        <ActionButtons onDownload={handleDownload} onCopy={handleCopy} onShare={() => setShowMenu(!showMenu)} />
      </div>
    );
  }

  // Video player
  if (isVideo && url) {
    return (
      <div className="relative group">
        <video src={url} controls className="w-48 h-32 rounded-lg border object-cover" />
        <ActionButtons onDownload={handleDownload} onCopy={handleCopy} onShare={() => setShowMenu(!showMenu)} />
        {showMenu && <ShareMenu onShare={handleShare} onCopy={handleCopy} onDownload={handleDownload} onClose={() => setShowMenu(false)} />}
      </div>
    );
  }

  // Audio player
  if (isAudio && url) {
    return (
      <div className="relative group flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
        <Mic size={14} className="text-green-600 shrink-0" />
        <audio src={url} controls className="h-8 w-40" />
        <ActionButtons onDownload={handleDownload} onCopy={handleCopy} onShare={() => setShowMenu(!showMenu)} />
        {showMenu && <ShareMenu onShare={handleShare} onCopy={handleCopy} onDownload={handleDownload} onClose={() => setShowMenu(false)} />}
      </div>
    );
  }

  // Generic file — with preview for PDFs
  const isPdf = (attachment.file_name || '').toLowerCase().endsWith('.pdf');
  return (
    <div className="relative group">
      <div className="flex items-center gap-2 bg-gray-50 border rounded-lg px-2.5 py-1.5 text-xs hover:bg-gray-100 cursor-pointer" onClick={() => isPdf ? setShowPreview(true) : handleDownload()}>
        <FileText size={16} className={isPdf ? 'text-red-500' : 'text-gray-500'} />
        <span className="max-w-[120px] truncate">{attachment.file_name || 'File'}</span>
        <Eye size={12} className="text-gray-400" />
      </div>
      <ActionButtons onDownload={handleDownload} onCopy={handleCopy} onShare={() => setShowMenu(!showMenu)} />
      {showMenu && <ShareMenu onShare={handleShare} onCopy={handleCopy} onDownload={handleDownload} onClose={() => setShowMenu(false)} />}
      {showPreview && isPdf && url && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center p-4" onClick={() => setShowPreview(false)}>
          <button className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 z-10">&times;</button>
          <iframe src={url} className="w-full max-w-3xl flex-1 rounded-lg bg-white" title={attachment.file_name} onClick={e => e.stopPropagation()} />
          <div className="flex gap-3 mt-3">
            <button onClick={(e) => { e.stopPropagation(); handleDownload(); }} className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm">Download</button>
            <button onClick={(e) => { e.stopPropagation(); handleShare('whatsapp'); }} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm">WhatsApp</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Small action buttons overlay
function ActionButtons({ onDownload, onCopy, onShare }) {
  return (
    <div className="flex items-center gap-0.5 mt-1">
      <button onClick={onDownload} className="p-1 hover:bg-gray-200 rounded" title="Download"><Download size={12} className="text-gray-500" /></button>
      <button onClick={onCopy} className="p-1 hover:bg-gray-200 rounded" title="Copy link"><Copy size={12} className="text-gray-500" /></button>
      <button onClick={onShare} className="p-1 hover:bg-gray-200 rounded" title="Share"><Share2 size={12} className="text-gray-500" /></button>
    </div>
  );
}

// Share dropdown
function ShareMenu({ onShare, onCopy, onDownload, onClose }) {
  return (
    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border z-20 py-1 w-40">
      {navigator.share && <button onClick={() => { onShare('native'); onClose(); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"><Share2 size={14} /> Share...</button>}
      <button onClick={() => { onShare('whatsapp'); onClose(); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"><span className="text-green-500 text-sm">●</span> WhatsApp</button>
      <button onClick={() => { onShare('imessage'); onClose(); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"><span className="text-blue-500 text-sm">●</span> iMessage</button>
      <button onClick={() => { onCopy(); onClose(); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"><Copy size={14} /> Copy link</button>
      <button onClick={() => { onDownload(); onClose(); }} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2"><Download size={14} /> Download</button>
    </div>
  );
}

// Full-screen image preview
function ImagePreview({ url, name, onClose, onDownload, onShare, onCopy }) {
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <button className="absolute top-4 right-4 text-white text-2xl hover:text-gray-300 z-10">&times;</button>
      <img src={url} alt={name || 'Preview'} className="max-w-full max-h-[90vh] rounded-lg object-contain" onClick={e => e.stopPropagation()} />
      <div className="absolute bottom-4 flex gap-3">
        <button onClick={(e) => { e.stopPropagation(); onDownload(); }} className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30">Download</button>
        <button onClick={(e) => { e.stopPropagation(); onShare('whatsapp'); }} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">WhatsApp</button>
        <button onClick={(e) => { e.stopPropagation(); onCopy(); }} className="px-4 py-2 bg-white/20 text-white rounded-lg text-sm hover:bg-white/30">Copy Link</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// OFFLINE BANNER
// ═══════════════════════════════════════
export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = async () => {
      setIsOffline(false);
      setSyncing(true);
      try { const result = await processQueue(api); if (result.processed > 0) toast.success(`Synced ${result.processed} items`); setQueueCount(result.remaining); } catch {}
      setSyncing(false);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    getQueue().then(items => setQueueCount(items.length));
    onSyncSuccess(() => { getQueue().then(items => setQueueCount(items.length)); });
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, []);

  if (!isOffline && queueCount === 0) return null;

  return (
    <div className={`px-4 py-2 text-xs font-medium flex items-center justify-between ${isOffline ? 'bg-red-500 text-white' : 'bg-amber-400 text-amber-900'}`}>
      <span>{isOffline ? '⚡ Offline. Changes will sync when connected.' : syncing ? '⏳ Syncing...' : `📤 ${queueCount} items waiting to sync`}</span>
      {!isOffline && queueCount > 0 && !syncing && (
        <button onClick={async () => { setSyncing(true); const result = await processQueue(api); if (result.processed > 0) toast.success(`Synced ${result.processed}`); setQueueCount(result.remaining); setSyncing(false); }} className="px-2 py-0.5 bg-white/20 rounded text-xs">Sync now</button>
      )}
    </div>
  );
}
